import { useState } from 'react';
import { Link } from 'react-router-dom';
import { StatusCard, inputStyle } from '@/pages/PublicCheckoutPage';
import { Footer } from '@/components/PublicExtras';

interface CheckoutStatus {
  id: string;
  status: 'PENDING' | 'PAID' | 'FAILED' | 'EXPIRED';
  target: string;
  amount: string;
  currency: string;
  failureReason: string | null;
}

/** Lets a customer check back on a checkout later, by reference, without the original tab. */
export function PublicCheckoutStatusPage() {
  const [reference, setReference] = useState('');
  const [result, setResult] = useState<CheckoutStatus | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  async function check() {
    setLoading(true);
    setError(null);
    setResult(null);
    try {
      const res = await fetch(`/api/public/checkout/${reference.trim()}`);
      if (!res.ok) {
        const body = await res.json().catch(() => null);
        throw new Error(body?.detail ?? 'No transaction found for that reference.');
      }
      setResult(await res.json());
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }

  return (
    <div style={{ minHeight: '100vh', background: '#f5f6f8', fontFamily: 'system-ui, sans-serif' }}>
      <header className="pub-header" style={{
        background: '#1e2460', color: 'white',
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
      }}>
        <span style={{ fontWeight: 700, fontSize: 18 }}>⚡ Recharge</span>
        <Link to="/pay" style={{ color: '#c7d2fe', fontSize: 13, textDecoration: 'none' }}>
          ← Back to recharge
        </Link>
      </header>

      <div className="pub-main" style={{ maxWidth: 420, margin: '0 auto' }}>
        <h1 style={{ fontSize: 20, fontWeight: 700, marginBottom: 4, color: '#1e2460' }}>Check transaction</h1>
        <p style={{ fontSize: 13, color: '#666', marginBottom: 16 }}>
          Paste the reference you were given to see its status.
        </p>

        <div style={{ background: 'white', borderRadius: 12, padding: 20, boxShadow: '0 1px 3px rgba(0,0,0,0.08)' }}>
          <label style={{ display: 'block', marginBottom: 12 }}>
            <span style={{ fontSize: 12, color: '#555', display: 'block', marginBottom: 4 }}>
              Transaction reference
            </span>
            <input
              value={reference}
              onChange={(e) => setReference(e.target.value)}
              placeholder="e.g. 8b728206-af36-4f5f-9d3a-1372c277f79b"
              style={inputStyle}
            />
          </label>
          {error && <p style={{ color: '#b91c1c', fontSize: 13, marginBottom: 8 }}>{error}</p>}
          <button
            onClick={check}
            disabled={!reference.trim() || loading}
            style={{
              width: '100%', padding: '12px 0', borderRadius: 8, border: 'none',
              background: reference.trim() ? '#1e2460' : '#d1d5db', color: 'white', fontWeight: 600,
              fontSize: 15, cursor: reference.trim() ? 'pointer' : 'not-allowed',
            }}
          >
            {loading ? 'Checking…' : 'Check transaction'}
          </button>
        </div>

        {result && (
          <div style={{ marginTop: 16 }}>
            {result.status === 'PENDING' && (
              <StatusCard tone="pending" title="Still pending">
                Waiting for payment on {result.target} ({result.currency} {result.amount}).
              </StatusCard>
            )}
            {result.status === 'PAID' && (
              <StatusCard tone="success" title="Paid — recharge sent">
                {result.target} was recharged with {result.currency} {result.amount}.
              </StatusCard>
            )}
            {(result.status === 'FAILED' || result.status === 'EXPIRED') && (
              <StatusCard tone="failed" title={result.status === 'EXPIRED' ? 'Expired' : 'Failed'}>
                {result.failureReason ?? 'This payment did not go through.'}
              </StatusCard>
            )}
          </div>
        )}
      </div>

      <Footer />
    </div>
  );
}
